import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const register = async (username, password, invitationCode) => {
    await axios.post(`${API}/auth/register`, { 
      username, 
      password, 
      invitation_code: invitationCode,
      role: "mechanic"
    });
    return login(username, password);
  };

  const createUser = async (username, password, role) => {
    const response = await axios.post(`${API}/auth/create-user`, { 
      username, 
      password, 
      role 
    });
    return response.data;
  };

  const getUsers = async () => {
    const response = await axios.get(`${API}/auth/users`);
    return response.data;
  };

  const deleteUser = async (userId) => {
    const response = await axios.delete(`${API}/auth/users/${userId}`);
    return response.data;
  };

  const isAdmin = () => user?.role === "admin";
  const isMechanic = () => user?.role === "mechanic" || user?.role === "staff";
  const canPublish = () => user?.role === "admin" || user?.role === "mechanic" || user?.role === "staff";

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      createUser, 
      getUsers, 
      deleteUser,
      isAdmin,
      isMechanic,
      canPublish 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
